// @flow

import {useCallback, useEffect, useState} from 'react';
import {fetchIncomingPayment, type IncomingPayment} from '@tectonics/prs/incoming-payment';
import {initReactIntl} from '@tectonics/translate';
import {useControllerContext, useLocalTenancyAsTenant} from './context';
import {useLanguage} from '@tectonics/controller/language/LanguageContext';
import {getDefaultCountry} from '@tectonics/controller/language/language-service';
import initI18n from '@tectonics/i18n';

export function useFetchIncomingPayment(setIncomingPayment: (IncomingPayment) => void): [bool, ?Error, () => void] {
  const [fetching, setFetching] = useState<boolean>(true);
  const [fetchingError, setFetchingError] = useState<?Error>(null);
  const {hash} = useControllerContext();
  const tenant = useLocalTenancyAsTenant();
  const [reloadCounter, setReloadCounter] = useState<number>(0);
  const requestReload = useCallback(() => {
    setReloadCounter(reloadCounter => reloadCounter + 1);
  }, []);
  const {addCountry} = useLanguage();

  useEffect(() => {
    setFetching(true);
    setFetchingError(null);

    (async () => {
      try {
        const incomingPayment = await fetchIncomingPayment(hash, tenant);
        const {country} = incomingPayment;
        await addCountry(country);
        setIncomingPayment(incomingPayment);
        await initReactIntl(country);
        await initI18n(country);
        setFetching(false);
      } catch (e) {
        const defaultCountry = getDefaultCountry();
        await initReactIntl(defaultCountry);
        setFetchingError(e);
        setFetching(false);
      }
    })();
  }, [hash, setIncomingPayment, setFetching, tenant, reloadCounter]);

  return [fetching, fetchingError, requestReload];
}
